import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { Spacer } from '@/common/layout/SpacerLayout';
import { DateUtils } from '@/common/utils/DateUtils';

export const TestKpiStrip = ({ runTsISO, tests, requirements, suites }) => {
  const allSuitesPass = suites.length > 0 && suites.every(s => s.status === 'SUCCESS');
  const suitePassCount = suites.filter(s => s.status === 'SUCCESS').length;

  const reqCoverage = requirements.total > 0 ? (requirements.met / requirements.total) * 100 : 0;

  return (
    <>
      {/* Run Timestamp */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Latest Run: {DateUtils.convertUtcToLocal(runTsISO) ?? 'n/a'}
      </Typography>

      {/* KPI Cards */}
      <Spacer>
        {/* Overall Status */}
        <DashboardCard
          title="Overall Status"
          icon={
            <FontAwesomeIcon
              icon={allSuitesPass ? fas.faCircleCheck : fas.faCircleXmark}
            />
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: allSuitesPass ? 'success.main' : 'error.main',
              }}
            >
              {allSuitesPass ? 'PASS' : 'FAIL'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {suitePassCount} of {suites.length} suites passing
            </Typography>
          </Box>
        </DashboardCard>

        {/* Test Results */}
        <DashboardCard title="Test Results" icon={<FontAwesomeIcon icon={fas.faVial} />}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Pass */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {tests.pass}
                </Typography>
                <FontAwesomeIcon
                  icon={fas.faCheck}
                  style={{ fontSize: 14, color: '#2e7d32' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Passing
              </Typography>
            </Box>

            {/* Fail */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {tests.fail}
                </Typography>
                <FontAwesomeIcon
                  icon={fas.faXmark}
                  style={{ fontSize: 14, color: '#d32f2f' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Failing
              </Typography>
            </Box>

            {/* Not Run */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  {tests.notRun}
                </Typography>
                <FontAwesomeIcon
                  icon={fas.faMinus}
                  style={{ fontSize: 14, color: '#999' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Not Run
              </Typography>
            </Box>
          </Box>
        </DashboardCard>

        {/* Requirement Coverage */}
        <DashboardCard
          title="Requirement Coverage"
          icon={<FontAwesomeIcon icon={fas.faListCheck} />}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {requirements.met}
              </Typography>
              <Typography variant="h5" color="text.secondary">
                / {requirements.total}
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={reqCoverage}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: reqCoverage === 100 ? 'success.main' : 'primary.main',
                  borderRadius: 1,
                },
              }}
            />

            <Typography variant="caption" color="text.secondary">
              {reqCoverage.toFixed(1)}% coverage
            </Typography>
          </Box>
        </DashboardCard>
      </Spacer>
    </>
  );
};